import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  BillingInfo,
  PaymentMethod,
  SimplePickerList,
  TransactionCategory,
  useBillingContext,
} from '@shipstation/financial-services-ui-components';
import type { AdyenCheckoutData, BillingInfoData } from '@shipstation/financial-services-ui-components';

import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Text, TextType } from 'ui-components/components/text';
import { emptyObject } from 'ui-components/default-props/empty';
import { noop } from 'ui-components/default-props/noop';

import { useFsuiContext } from '../../../financial-services-ui/fsui-context-provider';
import { getBillingInfoFromShipFrom } from '../../../financial-services-ui/mapping';
import type { PaymentMethodDetails } from '../../../financial-services-ui/types/fsui';
import { usePaymentAndSubscriptionMegaContext } from '../../../pages/settings/payment-and-subscription/context';
import Global from '../../../store/global-selectors';
import { CarrierConnectionErrorSource, Workflows } from '../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { usePaymentMethodCreation } from '../hooks/use-payment-method-creation';
import { messages } from '../messages';
import style from './payment-method-section.module.less';

const CREDIT_CARD_ONLY_PAYMENT_TYPES = [PaymentMethod.CreditCard];

type ShipFromOriginAddress = {
  name?: string;
  company?: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  countryCode?: string;
  phone?: string;
};

type ShipFrom = {
  isDefault?: boolean;
  originAddress?: ShipFromOriginAddress;
};

type PaymentMethodSectionProps = {
  onPaymentMethodCreated: (paymentMethodId: string, billingInfo?: BillingInfoData, paymentMethod?: PaymentMethodDetails) => void;
  onSubmitFunctionReady?: (submitFn: (() => void) | null) => void;
  onProcessingChange?: (isProcessing: boolean) => void;
  onCreationError?: () => void;
};

export const PaymentMethodSection = ({
  onPaymentMethodCreated,
  onSubmitFunctionReady,
  onProcessingChange,
  onCreationError,
}: PaymentMethodSectionProps) => {
  const intl = useIntl();
  const { preloadedSession, isPreloadingSession, onPaymentCompleted, onError, state } = useFsuiContext();
  const { billingInfo, setBillingInfo } = useBillingContext();
  const trackCarrierConnectionStep = useTrackWorkflow(Workflows.CARRIER_CONNECTION_FLOW);

  const context = usePaymentAndSubscriptionMegaContext();
  const { paymentMethodDetails } = context;
  const reloadData = (context as { reloadData?: () => Promise<[unknown, PaymentMethodDetails[] | undefined]> }).reloadData;

  const sellerDefaultCurrencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const defaultShipFrom: ShipFrom | undefined = useSelector(Global.selectSellerDefaultShipFrom);
  const locale = intl.locale;

  const [initialPaymentMethodIds] = useState(() => paymentMethodDetails.map((method) => method.id));
  const [isBillingInfoSeeded, setIsBillingInfoSeeded] = useState(false);

  // Seed billing info from the seller's default ship-from, matching the settings Add Payment Method flow.
  // BillingInfo must not mount until this seed has run, or it initializes its edit/preview state from
  // the still-undefined billing info and never syncs once we set it a moment later.
  useEffect(() => {
    if (defaultShipFrom) {
      setBillingInfo(getBillingInfoFromShipFrom(defaultShipFrom.originAddress) || emptyObject, true);
    }
    setIsBillingInfoSeeded(true);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- seed once on mount only
  }, []);

  const setSubmitFunction = useCallback(
    (fn: (() => void) | null) => {
      onSubmitFunctionReady?.(fn);
    },
    [onSubmitFunctionReady],
  );

  // Set right before handleError fires for the polling-timeout case; read once and reset so a later,
  // unrelated error doesn't inherit a stale timeout source.
  const errorSourceRef = useRef(CarrierConnectionErrorSource.UNEXPECTED_ERROR);

  const handleError = useCallback(() => {
    if (!preloadedSession) {
      return;
    }

    const errorSource = errorSourceRef.current;
    errorSourceRef.current = CarrierConnectionErrorSource.UNEXPECTED_ERROR;

    trackCarrierConnectionStep(WorkflowSteps.ERROR, { error_source: errorSource });

    if (onCreationError) {
      onCreationError();
      return;
    }
    onError(new Error('Payment method creation failed'));
  }, [onCreationError, onError, preloadedSession, trackCarrierConnectionStep]);

  const handleCreationTimeout = useCallback(() => {
    errorSourceRef.current = CarrierConnectionErrorSource.PAYMENT_METHOD_CREATION_TIMEOUT;
  }, []);

  // Use custom hook for payment method creation logic
  const { isProcessing, handlePaymentSuccess, updateBillingInfoRef } = usePaymentMethodCreation({
    previousPaymentMethodIds: initialPaymentMethodIds,
    reloadData,
    onSuccess: onPaymentMethodCreated,
    onError: handleError,
    onTimeout: handleCreationTimeout,
  });

  // Keep billing info ref updated
  useEffect(() => {
    updateBillingInfoRef(billingInfo);
  }, [billingInfo, updateBillingInfoRef]);

  // Surface the post-submit polling window so the modal footer can disable Back/Cancel/Save
  useEffect(() => {
    onProcessingChange?.(isProcessing);
  }, [isProcessing, onProcessingChange]);

  const handlePaymentCompleted = useCallback(
    (result: unknown) => {
      onPaymentCompleted(result as Record<string, unknown>, handlePaymentSuccess, handleError);
    },
    [onPaymentCompleted, handlePaymentSuccess, handleError],
  );

  const creditCardConfig = useMemo(
    () => ({
      currencyCode: sellerDefaultCurrencyCode,
      inputBorderRadius: '4px',
      inputHeight: '32px',
      locale,
      onError: handleError,
      onPaymentCompleted: handlePaymentCompleted,
      returnUrl: window.location.origin,
      // Passing the already-resolved session prevents CreditCard from self-fetching its own via
      // getSessionId, which raced FsuiContextProvider's auto-fetch and caused intermittent load failures.
      // The cast is safe because AuctanePaySession's declared type just omits a few AdyenCheckoutData fields
      // (activePaymentMethods, recentPaymentMethods, paymentMethodMetaTypeDetails, transactionCategory) that
      // CreditCard/useAdyenCheckout never actually read.
      session: (preloadedSession as AdyenCheckoutData | null) ?? undefined,
      submitPayment: setSubmitFunction,
      transactionCategory: TransactionCategory.Freight,
      displayMode: 'compact' as const,
    }),
    [handleError, handlePaymentCompleted, preloadedSession, setSubmitFunction, locale, sellerDefaultCurrencyCode],
  );

  const payByBankUsConfig = useMemo(
    () => ({
      currencyCode: sellerDefaultCurrencyCode,
      locale,
      session: preloadedSession,
      onError: handleError,
      onPaymentCompleted: noop,
      returnUrl: window.location.href,
      submitPayment: setSubmitFunction,
      transactionCategory: TransactionCategory.Freight,
    }),
    [handleError, locale, preloadedSession, setSubmitFunction, sellerDefaultCurrencyCode],
  );

  return (
    <FlexColumn gap={Gap.Space16}>
      <Text textType={TextType.H4} value={messages.paymentMethodSectionTitle} />

      {isPreloadingSession || (!preloadedSession && !state.error) ? (
        <LoadingIndicator className={style.loader} />
      ) : (
        <>
          <SimplePickerList
            creditCardConfig={creditCardConfig}
            currencyCode={sellerDefaultCurrencyCode}
            locale={locale}
            payByBankConfig={payByBankUsConfig}
            paymentTypes={CREDIT_CARD_ONLY_PAYMENT_TYPES}
            selectedType={PaymentMethod.CreditCard}
            setSelectedType={noop}
          />
          {isBillingInfoSeeded && (
            <div className={style.billingInfoPreview}>
              <BillingInfo setPerformFormValidation={noop} />
            </div>
          )}
        </>
      )}
    </FlexColumn>
  );
};
